import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DownloadIcon } from '@heroicons/react/outline';
import { useAsyncFn } from 'react-use';
import { AccountEntity } from '@shared/api/accountConfig/accountConfig.interface';
import {
  bugoAgencyDeliveryPopulate,
  giftBrandSellerDeliveryPopulate,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import { GiftDeliveryForAdmin } from '@shared/api/giftDelivery/giftDelivery.interface';
import { SettlementInfo } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import {
  deleteGiftRebateSettlement,
  listGiftRebateSettlements,
  patchGiftRebateSettlement,
  settleGiftRebate,
} from '@shared/api/giftRebateSettlement/giftRebateSettlement.controller';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import {
  deleteGiftSupplySettlement,
  listGiftSupplySettlements,
  patchGiftSupplySettlement,
  settleGiftSupply,
} from '@shared/api/giftSupplySettlement/giftSupplySettlement.controller';
import {
  GiftSettlementStatus,
  GiftSupplySettlement,
  SettlementPendingEntry,
} from '@shared/api/giftSupplySettlement/giftSupplySettlement.interface';
import { getSettlementPending } from '@shared/api/settlementPending/settlementPending.controller';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';

import { SupplySettlementDetailModal } from '../../giftBrand/components/SupplySettlementDetailModal';
import { RebateHistoryDetailModal } from '../components/agencyGiftSettlement/RebateHistoryDetailModal';
import { SettlementEditModal } from '../components/giftSettlement/SettlementEditModal';
import { formatDate, getItemName } from '../utils/delivery.utils';
import {
  exportRebateHistorySummaryExcel,
  exportSupplyHistorySummaryExcel,
} from '../utils/giftSettlementHistoryExcel';
import { DEFAULT_REBATE_FEE_RATE } from '../utils/rebate-table.utils';
import {
  computeSettlementLines,
  formatWon,
  getSettlementBrandId,
  ceilWon,
  settlementDisplayTotal,
  SettlementLine,
} from '../utils/settlement.utils';

// ───────────────────────── 공통 타입 / 헬퍼 ─────────────────────────

type MainTab = 'pending' | 'history';
type SettleType = 'supply' | 'rebate';

const toYmd = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const statusLabel = (s: GiftSettlementStatus) =>
  s === GiftSettlementStatus.Paid ? '지급완료' : '정산집계';

const statusColor = (s: GiftSettlementStatus) =>
  s === GiftSettlementStatus.Paid
    ? 'text-green-700 bg-green-50'
    : 'text-amber-700 bg-amber-50';

// bugoBrand populated 타입 (GiftItemForStore.bugoBrand는 string으로 선언되지만 실제론 populate됨)
type PopulatedBugoBrand = {
  _id: string;
  bugoAgency?: { corpName?: string; nickName?: string };
  giftAccountConfig?: { viaMari?: boolean };
};

const getBugoBrandId = (d: GiftDeliveryForAdmin): string | undefined => {
  if (typeof d.giftItem !== 'object') return undefined;
  const bb = (d.giftItem as any).bugoBrand;
  if (!bb) return undefined;
  if (typeof bb === 'string') return bb;
  return (bb as PopulatedBugoBrand)._id;
};

const getBugoBrandName = (d: GiftDeliveryForAdmin): string => {
  if (typeof d.giftItem !== 'object') return '미확인';
  const bb = (d.giftItem as any).bugoBrand as PopulatedBugoBrand | string | undefined;
  if (!bb) return '미확인';
  if (typeof bb === 'string') return bb;
  return bb.bugoAgency?.corpName || bb.bugoAgency?.nickName || bb._id;
};

const getBugoBrandViaMari = (d: GiftDeliveryForAdmin): boolean | undefined => {
  if (typeof d.giftItem !== 'object') return undefined;
  const bb = (d.giftItem as any).bugoBrand as PopulatedBugoBrand | string | undefined;
  if (!bb || typeof bb === 'string') return undefined;
  return bb.giftAccountConfig?.viaMari;
};

// 상조 정산단가 분해(판매가 − 도매가 − 수수료).
// 백엔드 preview(p.unitAmount)와 동일한 소스로 계산해 "판매가 − 도매가 − 수수료 = 정산단가"가
// 항상 맞아떨어지게 한다(판매가: 주문 스냅샷 우선, 도매가: giftItem.priceWhole — settle 동결 기준).
interface RebateBreakdown {
  priceRetail: number | null;
  priceWhole: number | null;
  /** FeeRate 방식 건당 수수료(판매가 × feeRate%). Rebate 방식이면 null */
  fee: number | null;
  feeRate: number;
  isRebateMethod: boolean;
}

const getRebateBreakdown = (d: GiftDeliveryForAdmin): RebateBreakdown => {
  const gi = typeof d.giftItem === 'object' ? (d.giftItem as any) : undefined;
  const cfg = gi?.bugoBrand?.giftAccountConfig;
  const feeRate = cfg?.feeRate ?? DEFAULT_REBATE_FEE_RATE;
  const isRebateMethod = cfg?.method === 'Rebate';
  const order = typeof d.giftOrder === 'object' ? (d.giftOrder as any) : undefined;
  const detail = order?.orderDetails?.find(
    (od: any) => String(od.giftItem) === String(gi?._id),
  );
  const priceRetail = detail?.giftItemEmbed?.priceRetail ?? gi?.priceRetail ?? null;
  const priceWhole = gi?.priceWhole ?? gi?.giftItemInfo?.priceWhole ?? null;
  const fee =
    !isRebateMethod && priceRetail != null ? (priceRetail * feeRate) / 100 : null;
  return { priceRetail, priceWhole, fee, feeRate, isRebateMethod };
};

const getSupplyAccountEntity = (d: GiftDeliveryForAdmin): string | undefined => {
  const brand = d.giftItemBrand;
  if (!brand || typeof brand !== 'object') return undefined;
  return (brand as any).settlementInfo?.accountEntity as string | undefined;
};

const accountEntityBadge = (entity: string | undefined) => {
  if (!entity || entity === 'NotDecided') return null;
  const isMari = entity === 'Mari';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
        isMari ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'
      }`}
    >
      {isMari ? '마리' : '가온'} 경유
    </span>
  );
};

const viaMarieBadge = (viaMari: boolean | undefined) => {
  if (viaMari === undefined) return null;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
        viaMari ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {viaMari ? '마리 경유' : '가온 직접'}
    </span>
  );
};

// ───────────────────────── Toast 훅 ─────────────────────────

const useToast = () => {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((msg: string) => {
    setMessage(msg);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(null), 4000);
  }, []);

  return { message, show };
};

// ───────────────────────── 집행 모달 ─────────────────────────

interface SettleModalProps {
  open: boolean;
  targetName: string;
  count: number;
  /** 선택 배송 계산 합계(반올림 전) */
  amount: number;
  onClose: () => void;
  onConfirm: (args: {
    year: number;
    month: number;
    memo: string;
    /** 실제 정산액(신규 정산 생성 시 계산값과 다르면 동결) */
    overrideAmount: number;
  }) => Promise<void>;
}

const SettleModal: FC<SettleModalProps> = ({
  open,
  targetName,
  count,
  amount,
  onClose,
  onConfirm,
}) => {
  // 계산값(반올림) — 실제 정산액 입력의 기본값/참조값
  const computed = ceilWon(amount);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [paid, setPaid] = useState<number>(computed);
  const [memo, setMemo] = useState('');
  const [loading, setLoading] = useState(false);

  // 모달은 항상 트리에 남아있어 재오픈 시 값이 초기화되지 않으므로, 열릴 때 오늘/계산금액으로 리셋한다.
  useEffect(() => {
    if (open) {
      setYear(new Date().getFullYear());
      setMonth(new Date().getMonth() + 1);
      setPaid(ceilWon(amount));
      setMemo('');
    }
  }, [open, amount]);

  if (!open) return null;

  const diff = paid - computed;
  // 정산예정일: 정산월 다음달 15일 (앱 다른 화면과 동일한 도출 규칙)
  const payoutYear = month === 12 ? year + 1 : year;
  const payoutMonth = month === 12 ? 1 : month + 1;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm({ year, month, memo, overrideAmount: paid });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-base font-bold text-gray-800">정산 집행</h3>
        <div className="mb-4 space-y-2 text-sm text-gray-600">
          <div>
            <span className="font-medium">대상:</span> {targetName}
          </div>
          <div>
            <span className="font-medium">배송 건수:</span> {count}건
          </div>
          <div>
            <span className="font-medium">계산 금액:</span> {formatWon(computed)}
          </div>
        </div>

        {/* 정산월 */}
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">정산월</span>
          <input
            type="number"
            value={year}
            min={2020}
            max={2099}
            onChange={(e) => setYear(Number(e.target.value))}
            className="w-20 rounded border border-gray-300 px-2 py-1 text-center"
          />
          <span>년</span>
          <input
            type="number"
            value={month}
            min={1}
            max={12}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="w-14 rounded border border-gray-300 px-2 py-1 text-center"
          />
          <span>월</span>
        </div>

        {/* 정산예정일 — 정산월 기준 자동 (저장하지 않는 안내값) */}
        <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
          <span className="font-medium text-gray-700">정산예정일</span>
          <span className="tabular-nums">
            {payoutYear}년 {payoutMonth}월 15일
          </span>
          <span className="text-xs text-gray-400">(정산월 기준 자동)</span>
        </div>

        {/* 실제 정산액 */}
        <div className="mb-1 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">실제 정산액</span>
          <input
            type="number"
            value={paid}
            min={0}
            step={1}
            onChange={(e) => setPaid(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            className="w-36 rounded border border-gray-300 px-2 py-1 text-right tabular-nums"
          />
          <span>원</span>
        </div>
        {diff !== 0 ? (
          <p className="mb-3 text-xs text-amber-600">
            계산 금액과 {formatWon(Math.abs(diff))} {diff > 0 ? '많음' : '적음'} — 신규 정산은 이 금액으로 동결됩니다.
          </p>
        ) : (
          <p className="mb-3 text-xs text-gray-400">
            기존 월 정산에 합쳐지는 경우엔 입력한 금액 대신 자동 재계산됩니다.
          </p>
        )}

        {/* 메모 */}
        <div className="mb-5">
          <label className="mb-1 block text-sm font-medium text-gray-700">메모</label>
          <textarea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            rows={2}
            placeholder="정산 관련 메모 (선택)"
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? '집행 중...' : '집행 확인'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────── 지급완료(Paid) 확정 모달 ─────────────────────────

interface PaidConfirmModalProps {
  open: boolean;
  targetName: string;
  count: number;
  amount: number;
  onClose: () => void;
  /** executedAt: 선택한 지급일(ISO 문자열), paidAmount: 실제 지급액(계산값과 다르면 동결) */
  onConfirm: (executedAt: string, paidAmount: number) => Promise<void>;
}

const PaidConfirmModal: FC<PaidConfirmModalProps> = ({
  open,
  targetName,
  count,
  amount,
  onClose,
  onConfirm,
}) => {
  // 계산식 기준 금액 — 실제 지급액 입력의 기본값/참조값
  const computed = ceilWon(amount);
  const [date, setDate] = useState(() => toYmd(new Date()));
  const [paid, setPaid] = useState<number>(computed);
  const [loading, setLoading] = useState(false);

  // 모달은 항상 트리에 남아있어 재오픈 시 값이 초기화되지 않으므로, 열릴 때 오늘/계산금액으로 리셋한다.
  useEffect(() => {
    if (open) {
      setDate(toYmd(new Date()));
      setPaid(ceilWon(amount));
    }
  }, [open, amount]);

  if (!open) return null;

  const diff = paid - computed;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      // 선택한 날짜를 로컬 정오로 고정해 타임존에 따른 날짜 밀림을 방지한다.
      await onConfirm(new Date(`${date}T12:00:00`).toISOString(), paid);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-base font-bold text-gray-800">지급완료 확정</h3>
        <div className="mb-4 space-y-2 text-sm text-gray-600">
          <div>
            <span className="font-medium">대상:</span> {targetName}
          </div>
          <div>
            <span className="font-medium">정산 건수:</span> {count}건
          </div>
          <div>
            <span className="font-medium">계산 금액:</span> {formatWon(computed)}
          </div>
        </div>
        <div className="mb-2 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">실제 지급액</span>
          <input
            type="number"
            value={paid}
            min={0}
            step={1}
            onChange={(e) => setPaid(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            className="w-36 rounded border border-gray-300 px-2 py-1 text-right tabular-nums"
          />
          <span>원</span>
        </div>
        {diff !== 0 && (
          <p className="mb-3 text-xs text-amber-600">
            계산 금액과 {formatWon(Math.abs(diff))} {diff > 0 ? '많음' : '적음'} — 입력한 금액으로 확정됩니다.
          </p>
        )}
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-700">지급일</span>
          <input
            type="date"
            value={date}
            max={toYmd(new Date())}
            onChange={(e) => setDate(e.target.value)}
            className="rounded border border-gray-300 px-2 py-1"
          />
        </div>
        <p className="mb-5 text-xs text-gray-400">
          실제 지급(이체)한 금액과 날짜를 입력하세요. 확정 후에는 Draft로 되돌리기 전까지 수정할 수 없습니다.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? '처리 중...' : '지급완료 확정'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────── 날짜 필터 공통 UI ─────────────────────────

interface DateFilterProps {
  dateFrom: string;
  dateTo: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}

const DateFilter: FC<DateFilterProps> = ({ dateFrom, dateTo, onFrom, onTo }) => (
  <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
    <span className="text-gray-500">기간(배송완료일)</span>
    <input
      type="date"
      value={dateFrom}
      max={dateTo || undefined}
      onChange={(e) => onFrom(e.target.value)}
      className="rounded border border-gray-300 px-2 py-1"
    />
    <span className="text-gray-400">~</span>
    <input
      type="date"
      value={dateTo}
      min={dateFrom || undefined}
      onChange={(e) => onTo(e.target.value)}
      className="rounded border border-gray-300 px-2 py-1"
    />
    <button
      onClick={() => {
        const n = new Date();
        onFrom(toYmd(new Date(n.getFullYear(), n.getMonth(), 1)));
        onTo(toYmd(new Date(n.getFullYear(), n.getMonth() + 1, 0)));
      }}
      className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
    >
      이번달
    </button>
    <button
      onClick={() => {
        const n = new Date();
        onFrom(toYmd(new Date(n.getFullYear(), n.getMonth() - 1, 1)));
        onTo(toYmd(new Date(n.getFullYear(), n.getMonth(), 0)));
      }}
      className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-600 hover:bg-gray-50"
    >
      지난달
    </button>
    {(dateFrom || dateTo) && (
      <button
        onClick={() => {
          onFrom('');
          onTo('');
        }}
        className="rounded-full border border-gray-300 bg-white px-3 py-1 text-gray-500 hover:bg-gray-50"
      >
        전체기간 ✕
      </button>
    )}
  </div>
);

// ───────────────────────── 업체정산(supply) 예정 ─────────────────────────

interface PendingSectionProps {
  isAdmin: boolean;
  onSettled: () => void;
  /** GiftAgent(마리) 조회 뷰: 마리 정산건만 노출하고 경유 칩을 숨긴다 */
  giftAgentView?: boolean;
}

const SupplyPendingSection: FC<PendingSectionProps> = ({
  isAdmin,
  onSettled,
  giftAgentView = false,
}) => {
  const toast = useToast();
  const [{ loading, value: entries }, fetchPending] = useAsyncFn(
    () => getSettlementPending('supply', {}, LowerRole.Admin),
    [],
  );

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // 정산정보(settlementInfo)는 settlement-pending 응답의 delivery.giftItemBrand에
  // 이미 fully populate 되어 있다. 별도 /admin/gift-item-brand 호출(GiftAgent는 admin
  // 롤이 없어 401)을 하지 않고, 각 배송이 들고 온 공급사 정보에서 직접 모은다.
  const settlementByBrand = useMemo(() => {
    const map = new Map<string, SettlementInfo | undefined>();
    (entries ?? []).forEach((e) => {
      const brand = e.delivery.giftItemBrand;
      if (brand && typeof brand === 'object') {
        map.set(brand._id, (brand as { settlementInfo?: SettlementInfo }).settlementInfo);
      }
    });
    return (brandId?: string) => (brandId ? map.get(brandId) : undefined);
  }, [entries]);

  const allLines = useMemo(
    () =>
      computeSettlementLines(
        (entries ?? []).map((e) => e.delivery),
        settlementByBrand,
      ),
    [entries, settlementByBrand],
  );

  const lineByDelivery = useMemo(() => {
    const map = new Map<string, SettlementLine>();
    allLines.forEach((l) => map.set(l.delivery._id, l));
    return map;
  }, [allLines]);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<{
    open: boolean;
    brandId: string;
    brandName: string;
    ids: string[];
    amount: number;
  }>({ open: false, brandId: '', brandName: '', ids: [], amount: 0 });

  const filtered = useMemo(() => {
    const fromT = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toT = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
    return (entries ?? []).filter((e) => {
      // GiftAgent(마리)는 업체정산 주체가 마리인 건만 본다 (가온 계약 도매가 비노출)
      if (giftAgentView && getSupplyAccountEntity(e.delivery) !== AccountEntity.Mari) {
        return false;
      }
      if (fromT !== null || toT !== null) {
        const at = e.delivery.shipping?.deliveredAt;
        if (!at) return false;
        const t = new Date(at).getTime();
        if (fromT !== null && t < fromT) return false;
        if (toT !== null && t > toT) return false;
      }
      return true;
    });
  }, [entries, dateFrom, dateTo, giftAgentView]);

  const groups = useMemo(() => {
    const map = new Map<
      string,
      { brandName: string; accountEntity?: string; entries: SettlementPendingEntry[] }
    >();
    filtered.forEach((e) => {
      const id = getSettlementBrandId(e.delivery) ?? '__none__';
      const brand = e.delivery.giftItemBrand;
      const name =
        typeof brand === 'object' && brand
          ? brand.name
          : id === '__none__'
          ? '미지정'
          : id;
      const accountEntity = getSupplyAccountEntity(e.delivery);
      if (!map.has(id)) map.set(id, { brandName: name, accountEntity, entries: [] });
      map.get(id)!.entries.push(e);
    });
    return Array.from(map.entries()).map(([id, g]) => ({ id, ...g }));
  }, [filtered]);

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (groupEntries: SettlementPendingEntry[]) => {
    const ids = groupEntries.map((e) => e.delivery._id);
    const allSel = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSel) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleSettle = async ({
    year,
    month,
    memo,
    overrideAmount,
  }: {
    year: number;
    month: number;
    memo: string;
    overrideAmount: number;
  }) => {
    try {
      // 같은 (업체, 정산월) 정산이 이미 있으면: Draft는 합치고, Paid는 차단·안내
      const existing = await listGiftSupplySettlements(
        { giftItemBrand: modal.brandId, year, month },
        [],
        {},
        LowerRole.Admin,
      );
      const dup = existing[0];
      if (dup) {
        if (dup.status === GiftSettlementStatus.Paid) {
          toast.show(
            `이미 지급완료된 ${year}년 ${month}월 정산이 있습니다. 수정하려면 먼저 Draft로 되돌리세요.`,
          );
          return;
        }
        // 기존 월 정산에 합칠 땐 전체 라인이 재계산되므로 override는 적용하지 않는다.
        await patchGiftSupplySettlement(dup._id, { addDeliveryIds: modal.ids });
        toast.show(`${year}년 ${month}월 정산에 ${modal.ids.length}건 추가 완료`);
      } else {
        const result = await settleGiftSupply({
          year,
          month,
          target: { giftItemBrand: modal.brandId },
          giftDeliveryIds: modal.ids,
          memo: memo || undefined,
        });
        const skipped = result.skipped.length;
        if (skipped > 0) {
          const reasons = Array.from(new Set(result.skipped.map((s) => s.reason))).join(', ');
          toast.show(`${result.includedCount}건 집행 완료. ${skipped}건 제외 (${reasons})`);
        } else {
          toast.show(`${result.includedCount}건 집행 완료`);
        }
        // 실제 정산액을 직접 입력했고 계산값과 다르면 그 금액으로 동결(신규 정산만)
        if (overrideAmount !== ceilWon(modal.amount)) {
          await patchGiftSupplySettlement(result.settlement._id, {
            overrideAmount: true,
            totalAmount: overrideAmount,
          });
        }
      }
      setModal((m) => ({ ...m, open: false }));
      setSelected(new Set());
      fetchPending();
      onSettled();
    } catch (e: any) {
      const msg = e?.response?.data?.message || '집행 실패';
      toast.show(`오류: ${msg}`);
      throw e;
    }
  };

  if (loading)
    return (
      <div className="center-box py-16">
        <Loading />
      </div>
    );

  return (
    <div>
      <DateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onFrom={setDateFrom}
        onTo={setDateTo}
      />

      {groups.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          미정산 내역이 없습니다.
        </div>
      ) : (
        groups.map((group) => {
          const groupIds = group.entries.map((e) => e.delivery._id);
          const selectedInGroup = groupIds.filter((id) => selected.has(id));
          const allGroupSel = groupIds.length > 0 && groupIds.every((id) => selected.has(id));
          const groupTotal = group.entries.reduce(
            (s, e) => s + (lineByDelivery.get(e.delivery._id)?.total ?? e.preview.lineAmount),
            0,
          );
          const selectedTotal = group.entries
            .filter((e) => selected.has(e.delivery._id))
            .reduce(
              (s, e) => s + (lineByDelivery.get(e.delivery._id)?.total ?? e.preview.lineAmount),
              0,
            );

          return (
            <div key={group.id} className="mb-6 overflow-x-auto rounded-lg bg-white shadow">
              <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <input
                      type="checkbox"
                      checked={allGroupSel}
                      onChange={() => toggleGroup(group.entries)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  )}
                  <span className="font-semibold text-gray-800">{group.brandName}</span>
                  {!giftAgentView && accountEntityBadge(group.accountEntity)}
                  <span className="text-xs text-gray-400">
                    {group.entries.length}건 · {formatWon(ceilWon(groupTotal))}
                  </span>
                </div>
                {isAdmin && (
                  <button
                    disabled={selectedInGroup.length === 0}
                    onClick={() =>
                      setModal({
                        open: true,
                        brandId: group.id,
                        brandName: group.brandName,
                        ids: selectedInGroup,
                        amount: selectedTotal,
                      })
                    }
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    선택 집행 ({selectedInGroup.length}건
                    {selectedInGroup.length > 0 ? ` · ${formatWon(selectedTotal)}` : ''})
                  </button>
                )}
              </div>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    {isAdmin && <th className="w-8 px-3 py-2" />}
                    <th className="px-3 py-2 text-left">상품</th>
                    <th className="px-3 py-2 text-left">배송요청일</th>
                    <th className="px-3 py-2 text-left">배송완료일</th>
                    <th className="px-3 py-2 text-left">택배사</th>
                    <th className="px-3 py-2 text-left">송장번호</th>
                    <th className="px-3 py-2 text-right">도매단가(+소량)</th>
                    <th className="px-3 py-2 text-right">× 수량</th>
                    <th className="px-3 py-2 text-right">+ 택배비</th>
                    <th className="px-3 py-2 text-right">= 정산금액</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {group.entries.map(({ delivery: d, preview: p }) => {
                    const l = lineByDelivery.get(d._id);
                    return (
                      <tr
                        key={d._id}
                        className={`hover:bg-gray-50 ${selected.has(d._id) ? 'bg-indigo-50' : ''}`}
                        onClick={() => isAdmin && toggleRow(d._id)}
                        style={{ cursor: isAdmin ? 'pointer' : undefined }}
                      >
                        {isAdmin && (
                          <td className="px-3 py-2.5">
                            <input
                              type="checkbox"
                              checked={selected.has(d._id)}
                              readOnly
                              className="h-4 w-4 rounded border-gray-300"
                            />
                          </td>
                        )}
                        <td className="px-3 py-3">
                          <p className="max-w-[160px] truncate font-medium">{getItemName(d)}</p>
                          <p className="text-[11px] text-gray-400">{d.recipient?.name || '-'}</p>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                          {formatDate(d.recipient?.submittedAt)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-xs font-semibold text-gray-800">
                          {formatDate(d.shipping?.deliveredAt)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-600">
                          {d.shipping?.carrier || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-600">
                          {d.shipping?.trackingNumber || '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                          {(l?.wholePrice ?? p.unitAmount).toLocaleString()}
                          {l && l.extraUnitPrice > 0 && (
                            <span className="text-amber-600">
                              (+{l.extraUnitPrice.toLocaleString()})
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                          {l?.quantity ?? p.quantity}개
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                          {l ? (
                            l.courierFee > 0 ? (
                              l.courierFee.toLocaleString()
                            ) : l.courierFreeByQuantity ? (
                              <span className="text-[11px] text-gray-400">
                                무료({l.courierFreeMinQuantity}충족)
                              </span>
                            ) : l.courierBundled ? (
                              <span className="text-[11px] text-gray-400">묶음면제</span>
                            ) : l.courierFeeBase > 0 ? (
                              '0'
                            ) : (
                              <span className="text-gray-300">-</span>
                            )
                          ) : p.surcharge > 0 ? (
                            <span className="text-amber-600">+{p.surcharge.toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right">
                          <span className="font-semibold text-gray-800 tabular-nums">
                            {formatWon(l?.total ?? p.lineAmount)}
                          </span>
                          {!(l?.hasSettlementInfo ?? p.hasSettlementInfo) && (
                            <span className="block text-[10px] text-amber-600">
                              정산정보 미설정
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 text-sm font-semibold">
                  <tr>
                    <td
                      className="px-3 py-2.5 text-gray-500"
                      colSpan={isAdmin ? 7 : 6}
                    >
                      합계 ({group.entries.length}건)
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-500">
                      {group.entries.reduce(
                        (s, e) =>
                          s + (lineByDelivery.get(e.delivery._id)?.quantity ?? e.preview.quantity),
                        0,
                      )}개
                    </td>
                    <td className="px-3 py-2.5" />
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-rose-700">
                      {formatWon(ceilWon(groupTotal))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })
      )}

      <SettleModal
        open={modal.open}
        targetName={modal.brandName}
        count={modal.ids.length}
        amount={modal.amount}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onConfirm={handleSettle}
      />
      {toast.message && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast.message}
        </div>
      )}
    </div>
  );
};

// ───────────────────────── 상조정산(rebate) 예정 ─────────────────────────

const RebatePendingSection: FC<PendingSectionProps> = ({
  isAdmin,
  onSettled,
  giftAgentView = false,
}) => {
  const toast = useToast();
  const [{ loading, value: entries }, fetchPending] = useAsyncFn(
    () => getSettlementPending('rebate', {}, LowerRole.Admin),
    [],
  );

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<{
    open: boolean;
    bugoBrandId: string;
    bugoBrandName: string;
    ids: string[];
    amount: number;
  }>({ open: false, bugoBrandId: '', bugoBrandName: '', ids: [], amount: 0 });

  const filtered = useMemo(() => {
    const fromT = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toT = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
    return (entries ?? []).filter((e) => {
      // GiftAgent(마리)는 마리가 상조에 지급하는 건(viaMari)만 본다
      if (giftAgentView && getBugoBrandViaMari(e.delivery) !== true) {
        return false;
      }
      if (fromT !== null || toT !== null) {
        const at = e.delivery.shipping?.deliveredAt;
        if (!at) return false;
        const t = new Date(at).getTime();
        if (fromT !== null && t < fromT) return false;
        if (toT !== null && t > toT) return false;
      }
      return true;
    });
  }, [entries, dateFrom, dateTo, giftAgentView]);

  const groups = useMemo(() => {
    const map = new Map<
      string,
      { name: string; viaMari?: boolean; entries: SettlementPendingEntry[] }
    >();
    filtered.forEach((e) => {
      const id = getBugoBrandId(e.delivery) ?? '__none__';
      const name =
        id === '__none__' ? '미지정' : getBugoBrandName(e.delivery);
      const viaMari = getBugoBrandViaMari(e.delivery);
      if (!map.has(id)) map.set(id, { name, viaMari, entries: [] });
      map.get(id)!.entries.push(e);
    });
    return Array.from(map.entries()).map(([id, g]) => ({ id, ...g }));
  }, [filtered]);

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (groupEntries: SettlementPendingEntry[]) => {
    const ids = groupEntries.map((e) => e.delivery._id);
    const allSel = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSel) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleSettle = async ({
    year,
    month,
    memo,
    overrideAmount,
  }: {
    year: number;
    month: number;
    memo: string;
    overrideAmount: number;
  }) => {
    try {
      // 같은 (상조, 정산월) 정산이 이미 있으면: Draft는 합치고, Paid는 차단·안내
      const existing = await listGiftRebateSettlements(
        { bugoBrand: modal.bugoBrandId, year, month },
        [],
        {},
        LowerRole.Admin,
      );
      const dup = existing[0];
      if (dup) {
        if (dup.status === GiftSettlementStatus.Paid) {
          toast.show(
            `이미 지급완료된 ${year}년 ${month}월 정산이 있습니다. 수정하려면 먼저 Draft로 되돌리세요.`,
          );
          return;
        }
        // 기존 월 정산에 합칠 땐 전체 라인이 재계산되므로 override는 적용하지 않는다.
        await patchGiftRebateSettlement(dup._id, { addDeliveryIds: modal.ids });
        toast.show(`${year}년 ${month}월 정산에 ${modal.ids.length}건 추가 완료`);
      } else {
        const result = await settleGiftRebate({
          year,
          month,
          target: { bugoBrand: modal.bugoBrandId },
          giftDeliveryIds: modal.ids,
          memo: memo || undefined,
        });
        const skipped = result.skipped.length;
        if (skipped > 0) {
          const reasons = Array.from(new Set(result.skipped.map((s) => s.reason))).join(', ');
          toast.show(`${result.includedCount}건 집행 완료. ${skipped}건 제외 (${reasons})`);
        } else {
          toast.show(`${result.includedCount}건 집행 완료`);
        }
        // 실제 정산액을 직접 입력했고 계산값과 다르면 그 금액으로 동결(신규 정산만)
        if (overrideAmount !== ceilWon(modal.amount)) {
          await patchGiftRebateSettlement(result.settlement._id, {
            overrideAmount: true,
            totalAmount: overrideAmount,
          });
        }
      }
      setModal((m) => ({ ...m, open: false }));
      setSelected(new Set());
      fetchPending();
      onSettled();
    } catch (e: any) {
      const msg = e?.response?.data?.message || '집행 실패';
      toast.show(`오류: ${msg}`);
      throw e;
    }
  };

  if (loading)
    return (
      <div className="center-box py-16">
        <Loading />
      </div>
    );

  return (
    <div>
      <DateFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onFrom={setDateFrom}
        onTo={setDateTo}
      />

      {groups.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          미정산 내역이 없습니다.
        </div>
      ) : (
        groups.map((group) => {
          const groupIds = group.entries.map((e) => e.delivery._id);
          const selectedInGroup = groupIds.filter((id) => selected.has(id));
          const allGroupSel = groupIds.length > 0 && groupIds.every((id) => selected.has(id));
          const groupTotal = group.entries.reduce((s, e) => s + e.preview.lineAmount, 0);
          const selectedTotal = group.entries
            .filter((e) => selected.has(e.delivery._id))
            .reduce((s, e) => s + e.preview.lineAmount, 0);
          // 정산단가 분해(판매가 − 도매가 − 수수료) 합계 — 푸터 표시용
          const bds = group.entries.map((e) => ({
            ...getRebateBreakdown(e.delivery),
            quantity: e.preview.quantity,
          }));
          const sumRetail = bds.reduce((s, b) => s + (b.priceRetail ?? 0) * b.quantity, 0);
          const sumWhole = bds.reduce((s, b) => s + (b.priceWhole ?? 0) * b.quantity, 0);
          const sumFee = Math.floor(
            bds.reduce((s, b) => s + (b.fee ?? 0) * b.quantity, 0),
          );

          return (
            <div key={group.id} className="mb-6 overflow-x-auto rounded-lg bg-white shadow">
              <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-2.5">
                <div className="flex items-center gap-3">
                  {isAdmin && (
                    <input
                      type="checkbox"
                      checked={allGroupSel}
                      onChange={() => toggleGroup(group.entries)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  )}
                  <span className="font-semibold text-gray-800">{group.name}</span>
                  {!giftAgentView && viaMarieBadge(group.viaMari)}
                  <span className="text-xs text-gray-400">
                    {group.entries.length}건 · {formatWon(ceilWon(groupTotal))}
                  </span>
                </div>
                {isAdmin && (
                  <button
                    disabled={selectedInGroup.length === 0}
                    onClick={() =>
                      setModal({
                        open: true,
                        bugoBrandId: group.id,
                        bugoBrandName: group.name,
                        ids: selectedInGroup,
                        amount: selectedTotal,
                      })
                    }
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    선택 집행 ({selectedInGroup.length}건
                    {selectedInGroup.length > 0 ? ` · ${formatWon(selectedTotal)}` : ''})
                  </button>
                )}
              </div>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    {isAdmin && <th className="w-8 px-3 py-2" />}
                    <th className="px-3 py-2 text-left">상품</th>
                    <th className="px-3 py-2 text-left">배송요청일</th>
                    <th className="px-3 py-2 text-left">배송완료일</th>
                    <th className="px-3 py-2 text-right">상품판매가</th>
                    <th className="px-3 py-2 text-right">상품도매가</th>
                    <th className="px-3 py-2 text-right">수수료</th>
                    <th className="px-3 py-2 text-right">= 정산단가</th>
                    <th className="px-3 py-2 text-right">× 수량</th>
                    <th className="px-3 py-2 text-right">= 정산금액</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {group.entries.map(({ delivery: d, preview: p }) => {
                    const b = getRebateBreakdown(d);
                    return (
                      <tr
                        key={d._id}
                        className={`hover:bg-gray-50 ${selected.has(d._id) ? 'bg-indigo-50' : ''}`}
                        onClick={() => isAdmin && toggleRow(d._id)}
                        style={{ cursor: isAdmin ? 'pointer' : undefined }}
                      >
                        {isAdmin && (
                          <td className="px-3 py-2.5">
                            <input
                              type="checkbox"
                              checked={selected.has(d._id)}
                              readOnly
                              className="h-4 w-4 rounded border-gray-300"
                            />
                          </td>
                        )}
                        <td className="px-3 py-3">
                          <p className="max-w-[160px] truncate font-medium">{getItemName(d)}</p>
                          <p className="text-[11px] text-gray-400">{d.recipient?.name || '-'}</p>
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                          {formatDate(d.recipient?.submittedAt)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-xs font-semibold text-gray-800">
                          {formatDate(d.shipping?.deliveredAt)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                          {b.priceRetail != null ? b.priceRetail.toLocaleString() : '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                          {b.priceWhole != null ? b.priceWhole.toLocaleString() : '-'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                          {b.isRebateMethod ? (
                            <span className="text-[11px] text-gray-400">리베이트</span>
                          ) : b.fee != null ? (
                            <span className="text-rose-600">
                              −{Math.round(b.fee).toLocaleString()}
                              <span className="ml-0.5 text-[10px] text-gray-400">
                                ({b.feeRate}%)
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right font-medium tabular-nums">
                          {p.unitAmount.toLocaleString()}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums">
                          {p.quantity}개
                        </td>
                        <td className="whitespace-nowrap px-3 py-3 text-right">
                          <span className="font-semibold text-gray-800 tabular-nums">
                            {formatWon(p.lineAmount)}
                          </span>
                          {!p.hasSettlementInfo && (
                            <span className="block text-[10px] text-amber-600">정산정보 미설정</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 text-sm font-semibold">
                  <tr>
                    <td className="px-3 py-2.5 text-gray-500" colSpan={isAdmin ? 4 : 3}>
                      합계 ({group.entries.length}건)
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-500">
                      {sumRetail.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-500">
                      {sumWhole.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-rose-600">
                      {sumFee > 0 ? `−${sumFee.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-3 py-2.5" />
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-500">
                      {group.entries.reduce((s, e) => s + e.preview.quantity, 0)}개
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-rose-700">
                      {formatWon(ceilWon(groupTotal))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          );
        })
      )}

      <SettleModal
        open={modal.open}
        targetName={modal.bugoBrandName}
        count={modal.ids.length}
        amount={modal.amount}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onConfirm={handleSettle}
      />
      {toast.message && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast.message}
        </div>
      )}
    </div>
  );
};

// ───────────────────────── 업체정산(supply) 내역 ─────────────────────────

interface HistorySectionProps {
  isAdmin: boolean;
  refreshTrigger: number;
  /** GiftAgent(마리) 조회 뷰: 마리 정산건만 노출하고 경유 칼럼을 숨긴다 */
  giftAgentView?: boolean;
}

const STATUS_FILTERS: { key: 'all' | GiftSettlementStatus; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: GiftSettlementStatus.Draft, label: '정산집계' },
  { key: GiftSettlementStatus.Paid, label: '지급완료' },
];

const SupplyHistorySection: FC<HistorySectionProps> = ({
  isAdmin,
  refreshTrigger,
  giftAgentView = false,
}) => {
  const toast = useToast();
  // GiftAgent(마리)는 /admin이 아니라 /gift-agent 읽기 전용 prefix로 조회한다(백엔드가 마리 건만 스코프).
  const role = giftAgentView ? LowerRole.GiftAgent : LowerRole.Admin;
  const [{ loading, value: items }, fetchHistory] = useAsyncFn(
    () =>
      listGiftSupplySettlements(
        {},
        [
          { path: 'giftItemBrand', select: 'name settlementInfo' },
          // 상세보기 모달의 배송요청일/배송완료일/택배사/송장번호 표시용
          { path: 'lines.giftDelivery', populate: giftBrandSellerDeliveryPopulate },
        ],
        { sort: { year: -1, month: -1 } },
        role,
      ),
    [role],
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshTrigger]);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | GiftSettlementStatus>('all');
  const [editTarget, setEditTarget] = useState<GiftSupplySettlement | null>(null);
  // 상세보기 모달에 띄울 정산 문서 (null이면 닫힘)
  const [detailTarget, setDetailTarget] = useState<GiftSupplySettlement | null>(null);
  const [paidModal, setPaidModal] = useState<{
    open: boolean;
    id: string;
    name: string;
    count: number;
    amount: number;
  }>({ open: false, id: '', name: '', count: 0, amount: 0 });

  const handlePatch = async (
    id: string,
    status: GiftSettlementStatus,
    label: string,
    executedAt?: string,
    override?: { totalAmount: number },
  ): Promise<boolean> => {
    setActionLoading(id);
    try {
      await patchGiftSupplySettlement(id, {
        status,
        ...(executedAt ? { executedAt } : {}),
        ...(override ? { overrideAmount: true, totalAmount: override.totalAmount } : {}),
      });
      toast.show(`${label} 완료`);
      fetchHistory();
      return true;
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || label + ' 실패'}`);
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = useMemo(() => {
    let all = (items ?? []) as GiftSupplySettlement[];
    // GiftAgent(마리)는 정산 주체가 마리인 업체정산만 본다
    if (giftAgentView) {
      all = all.filter(
        (s) =>
          typeof s.giftItemBrand === 'object' &&
          s.giftItemBrand.settlementInfo?.accountEntity === AccountEntity.Mari,
      );
    }
    return statusFilter === 'all' ? all : all.filter((s) => s.status === statusFilter);
  }, [items, statusFilter, giftAgentView]);
  const summary = useMemo(
    () => ({
      count: filtered.length,
      total: filtered.reduce((sum, s) => sum + settlementDisplayTotal(s), 0),
    }),
    [filtered],
  );
  const editBrandId =
    editTarget &&
    (typeof editTarget.giftItemBrand === 'object'
      ? editTarget.giftItemBrand._id
      : editTarget.giftItemBrand);

  if (loading)
    return (
      <div className="center-box py-8">
        <Loading />
      </div>
    );
  if (!items?.length)
    return (
      <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
        정산 내역이 없습니다.
      </div>
    );

  return (
    <div>
      {/* 상태 필터 + 요약 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`rounded-full border px-3 py-1 text-sm ${
                statusFilter === f.key
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">
            {summary.count}건 · 합계{' '}
            <span className="font-semibold text-rose-700">{formatWon(summary.total)}</span>
          </p>
          <button
            onClick={() => exportSupplyHistorySummaryExcel(filtered, giftAgentView)}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <DownloadIcon className="h-4 w-4" />
            엑셀 다운로드
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2.5 text-left">업체</th>
              {!giftAgentView && <th className="px-3 py-2.5 text-left">정산 주체</th>}
              <th className="px-3 py-2.5 text-left">정산월</th>
              <th className="px-3 py-2.5 text-left">상태</th>
              <th className="px-3 py-2.5 text-right">건수</th>
              <th className="px-3 py-2.5 text-right">정산금액</th>
              <th className="px-3 py-2.5 text-left">지급일</th>
              <th className="px-3 py-2.5 text-left">메모</th>
              <th className="px-3 py-2.5 text-center">상세</th>
              {isAdmin && <th className="px-3 py-2.5 text-right">관리</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={(isAdmin ? 10 : 9) - (giftAgentView ? 1 : 0)}
                  className="px-3 py-10 text-center text-gray-400"
                >
                  해당 상태의 정산이 없습니다.
                </td>
              </tr>
            )}
            {filtered.map((s) => {
              const brand = s.giftItemBrand;
              const brandName = typeof brand === 'object' ? brand.name : (brand || '-');
              const entity =
                typeof brand === 'object' ? brand.settlementInfo?.accountEntity : undefined;
              const isPaid = s.status === GiftSettlementStatus.Paid;
              const isActing = actionLoading === s._id;

              return (
                <tr key={s._id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-700">
                    {brandName}
                  </td>
                  {!giftAgentView && (
                    <td className="px-3 py-3">{accountEntityBadge(entity)}</td>
                  )}
                  <td className="whitespace-nowrap px-3 py-3 font-medium">
                    {s.year}년 {s.month}월
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(s.status)}`}
                    >
                      {statusLabel(s.status)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                    {s.lines.length}건
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-gray-800">
                    {formatWon(settlementDisplayTotal(s))}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {s.executedAt ? formatDate(s.executedAt) : '-'}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-3 text-xs text-gray-500">
                    {s.memo || '-'}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => setDetailTarget(s)}
                      className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                    >
                      상세보기
                    </button>
                  </td>
                  {isAdmin && (
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setEditTarget(s)}
                          className="rounded border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          수정
                        </button>
                        {!isPaid && (
                          <button
                            disabled={isActing}
                            onClick={() =>
                              setPaidModal({
                                open: true,
                                id: s._id,
                                name: brandName,
                                count: s.lines.length,
                                amount: s.totalAmount,
                              })
                            }
                            className="rounded bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            {isActing ? '처리 중...' : '지급확정'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailTarget && (
        <SupplySettlementDetailModal
          settlement={detailTarget}
          mySettlementInfo={
            typeof detailTarget.giftItemBrand === 'object'
              ? (detailTarget.giftItemBrand.settlementInfo as SettlementInfo | undefined)
              : undefined
          }
          onClose={() => setDetailTarget(null)}
        />
      )}

      <PaidConfirmModal
        open={paidModal.open}
        targetName={paidModal.name}
        count={paidModal.count}
        amount={paidModal.amount}
        onClose={() => setPaidModal((m) => ({ ...m, open: false }))}
        onConfirm={async (executedAt, paidAmount) => {
          // 입력한 실제 지급액을 그대로 동결(override)해 확정한다 (재계산·반올림 없음 — 화면 금액 = 지급액).
          const ok = await handlePatch(
            paidModal.id,
            GiftSettlementStatus.Paid,
            'Paid 확정',
            executedAt,
            { totalAmount: paidAmount },
          );
          if (ok) setPaidModal((m) => ({ ...m, open: false }));
        }}
      />
      {editTarget && editBrandId && (
        <SettlementEditModal
          open
          purpose="supply"
          settlement={editTarget}
          targetName={
            typeof editTarget.giftItemBrand === 'object'
              ? editTarget.giftItemBrand.name
              : '업체정산'
          }
          fetchEligible={() =>
            getSettlementPending('supply', { giftItemBrand: editBrandId }, LowerRole.Admin)
          }
          patch={patchGiftSupplySettlement}
          remove={deleteGiftSupplySettlement}
          onClose={() => setEditTarget(null)}
          onSaved={fetchHistory}
        />
      )}
      {toast.message && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast.message}
        </div>
      )}
    </div>
  );
};

// ───────────────────────── 상조정산(rebate) 내역 ─────────────────────────

const RebateHistorySection: FC<HistorySectionProps> = ({
  isAdmin,
  refreshTrigger,
  giftAgentView = false,
}) => {
  const toast = useToast();
  // GiftAgent(마리)는 /admin이 아니라 /gift-agent 읽기 전용 prefix로 조회한다(백엔드가 viaMari 건만 스코프).
  const role = giftAgentView ? LowerRole.GiftAgent : LowerRole.Admin;
  const [{ loading, value: items }, fetchHistory] = useAsyncFn(
    () =>
      listGiftRebateSettlements(
        {},
        [
          { path: 'bugoBrand', select: 'bugoAgency giftAccountConfig' },
          // 상세보기 모달의 배송 메타(구매자/고인명/발인일/지도사/배송일) 표시용
          { path: 'lines.giftDelivery', populate: bugoAgencyDeliveryPopulate },
        ],
        { sort: { year: -1, month: -1 } },
        role,
      ),
    [role],
  );

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshTrigger]);

  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | GiftSettlementStatus>('all');
  const [editTarget, setEditTarget] = useState<GiftRebateSettlement | null>(null);
  // 상세보기 모달에 띄울 정산 문서 (null이면 닫힘)
  const [detailTarget, setDetailTarget] = useState<GiftRebateSettlement | null>(null);
  const [paidModal, setPaidModal] = useState<{
    open: boolean;
    id: string;
    name: string;
    count: number;
    amount: number;
  }>({ open: false, id: '', name: '', count: 0, amount: 0 });

  const handlePatch = async (
    id: string,
    status: GiftSettlementStatus,
    label: string,
    executedAt?: string,
    override?: { totalAmount: number },
  ): Promise<boolean> => {
    setActionLoading(id);
    try {
      await patchGiftRebateSettlement(id, {
        status,
        ...(executedAt ? { executedAt } : {}),
        ...(override ? { overrideAmount: true, totalAmount: override.totalAmount } : {}),
      });
      toast.show(`${label} 완료`);
      fetchHistory();
      return true;
    } catch (e: any) {
      toast.show(`오류: ${e?.response?.data?.message || label + ' 실패'}`);
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = useMemo(() => {
    let all = (items ?? []) as GiftRebateSettlement[];
    // GiftAgent(마리)는 마리가 지급하는 상조정산(viaMari)만 본다
    if (giftAgentView) {
      all = all.filter(
        (s) =>
          typeof s.bugoBrand === 'object' &&
          s.bugoBrand.giftAccountConfig?.viaMari === true,
      );
    }
    return statusFilter === 'all' ? all : all.filter((s) => s.status === statusFilter);
  }, [items, statusFilter, giftAgentView]);
  const summary = useMemo(
    () => ({
      count: filtered.length,
      total: filtered.reduce((sum, s) => sum + settlementDisplayTotal(s), 0),
    }),
    [filtered],
  );
  const editBugoBrandId =
    editTarget &&
    (typeof editTarget.bugoBrand === 'object'
      ? editTarget.bugoBrand._id
      : editTarget.bugoBrand);
  const editBugoBrandName =
    editTarget && typeof editTarget.bugoBrand === 'object'
      ? editTarget.bugoBrand.bugoAgency?.corpName ||
        editTarget.bugoBrand.bugoAgency?.nickName ||
        '상조정산'
      : '상조정산';

  if (loading)
    return (
      <div className="center-box py-8">
        <Loading />
      </div>
    );
  if (!items?.length)
    return (
      <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
        정산 내역이 없습니다.
      </div>
    );

  return (
    <div>
      {/* 상태 필터 + 요약 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`rounded-full border px-3 py-1 text-sm ${
                statusFilter === f.key
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-500">
            {summary.count}건 · 합계{' '}
            <span className="font-semibold text-rose-700">{formatWon(summary.total)}</span>
          </p>
          <button
            onClick={() => exportRebateHistorySummaryExcel(filtered, giftAgentView)}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <DownloadIcon className="h-4 w-4" />
            엑셀 다운로드
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-3 py-2.5 text-left">상조</th>
              {!giftAgentView && <th className="px-3 py-2.5 text-left">경유</th>}
              <th className="px-3 py-2.5 text-left">정산월</th>
              <th className="px-3 py-2.5 text-left">상태</th>
              <th className="px-3 py-2.5 text-right">건수</th>
              <th className="px-3 py-2.5 text-right">정산금액</th>
              <th className="px-3 py-2.5 text-left">지급일</th>
              <th className="px-3 py-2.5 text-left">메모</th>
              <th className="px-3 py-2.5 text-center">상세</th>
              {isAdmin && <th className="px-3 py-2.5 text-right">관리</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={(isAdmin ? 10 : 9) - (giftAgentView ? 1 : 0)}
                  className="px-3 py-10 text-center text-gray-400"
                >
                  해당 상태의 정산이 없습니다.
                </td>
              </tr>
            )}
            {filtered.map((s) => {
              const bb = s.bugoBrand;
              const bbName =
                typeof bb === 'object'
                  ? bb.bugoAgency?.corpName || bb.bugoAgency?.nickName || bb._id
                  : (bb || '-');
              const viaMari =
                typeof bb === 'object' ? bb.giftAccountConfig?.viaMari : undefined;
              const isPaid = s.status === GiftSettlementStatus.Paid;
              const isActing = actionLoading === s._id;

              return (
                <tr key={s._id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-700">
                    {bbName}
                  </td>
                  {!giftAgentView && (
                    <td className="px-3 py-3">{viaMarieBadge(viaMari)}</td>
                  )}
                  <td className="whitespace-nowrap px-3 py-3 font-medium">
                    {s.year}년 {s.month}월
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor(s.status)}`}
                    >
                      {statusLabel(s.status)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-gray-600">
                    {s.lines.length}건
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-gray-800">
                    {formatWon(settlementDisplayTotal(s))}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {s.executedAt ? formatDate(s.executedAt) : '-'}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-3 text-xs text-gray-500">
                    {s.memo || '-'}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => setDetailTarget(s)}
                      className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                    >
                      상세보기
                    </button>
                  </td>
                  {isAdmin && (
                    <td className="whitespace-nowrap px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setEditTarget(s)}
                          className="rounded border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        >
                          수정
                        </button>
                        {!isPaid && (
                          <button
                            disabled={isActing}
                            onClick={() =>
                              setPaidModal({
                                open: true,
                                id: s._id,
                                name: bbName,
                                count: s.lines.length,
                                amount: s.totalAmount,
                              })
                            }
                            className="rounded bg-green-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                          >
                            {isActing ? '처리 중...' : '지급확정'}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailTarget && (
        <RebateHistoryDetailModal
          settlement={detailTarget}
          onClose={() => setDetailTarget(null)}
        />
      )}

      <PaidConfirmModal
        open={paidModal.open}
        targetName={paidModal.name}
        count={paidModal.count}
        amount={paidModal.amount}
        onClose={() => setPaidModal((m) => ({ ...m, open: false }))}
        onConfirm={async (executedAt, paidAmount) => {
          // 입력한 실제 지급액을 그대로 동결(override)해 확정한다 (재계산·반올림 없음 — 화면 금액 = 지급액).
          const ok = await handlePatch(
            paidModal.id,
            GiftSettlementStatus.Paid,
            'Paid 확정',
            executedAt,
            { totalAmount: paidAmount },
          );
          if (ok) setPaidModal((m) => ({ ...m, open: false }));
        }}
      />
      {editTarget && editBugoBrandId && (
        <SettlementEditModal
          open
          purpose="rebate"
          settlement={editTarget}
          targetName={editBugoBrandName}
          fetchEligible={() =>
            getSettlementPending('rebate', { bugoBrand: editBugoBrandId }, LowerRole.Admin)
          }
          patch={patchGiftRebateSettlement}
          remove={deleteGiftRebateSettlement}
          onClose={() => setEditTarget(null)}
          onSaved={fetchHistory}
        />
      )}
      {toast.message && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gray-800 px-4 py-2 text-sm text-white shadow-lg">
          {toast.message}
        </div>
      )}
    </div>
  );
};

// ───────────────────────── 메인 페이지 ─────────────────────────

const SETTLE_TYPE_TABS: { key: SettleType; label: string }[] = [
  { key: 'supply', label: '업체정산' },
  { key: 'rebate', label: '상조정산' },
];

const MAIN_TABS: { key: MainTab; label: string }[] = [
  { key: 'pending', label: '미정산내역' },
  { key: 'history', label: '정산 완료 내역' },
];

export const GiftSettlementPage: FC = function GiftSettlementPage() {
  const { isAdmin, isGiftAgent } = useAuth();
  const canWrite = isAdmin() && !isGiftAgent();
  // GiftAgent(마리) 조회 뷰: 마리 정산건만 노출 + 경유 칩/칼럼 숨김
  const giftAgentView = !isAdmin() && isGiftAgent();

  const [mainTab, setMainTab] = useState<MainTab>('pending');
  const [settleType, setSettleType] = useState<SettleType>('supply');
  // 집행 후 내역 탭 강제 리프레시
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const handleSettled = useCallback(() => {
    setHistoryRefresh((n) => n + 1);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">정산 관리</h1>
          <p className="mt-1 text-sm text-gray-500">
            답례품 업체정산 및 상조정산 조회{canWrite ? ' · 집행 · 수정 · 확정' : ''}
          </p>
        </div>

        {/* 메인 탭 */}
        <div className="mb-5 border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            {MAIN_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setMainTab(t.key)}
                className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-semibold ${
                  mainTab === t.key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {/* 정산 종류 — 세그먼트 컨트롤 */}
        <div className="mb-6 inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
          {SETTLE_TYPE_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setSettleType(t.key)}
              className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                settleType === t.key
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {mainTab === 'pending' && settleType === 'supply' && (
          <SupplyPendingSection
            isAdmin={canWrite}
            onSettled={handleSettled}
            giftAgentView={giftAgentView}
          />
        )}
        {mainTab === 'pending' && settleType === 'rebate' && (
          <RebatePendingSection
            isAdmin={canWrite}
            onSettled={handleSettled}
            giftAgentView={giftAgentView}
          />
        )}
        {mainTab === 'history' && settleType === 'supply' && (
          <SupplyHistorySection
            isAdmin={canWrite}
            refreshTrigger={historyRefresh}
            giftAgentView={giftAgentView}
          />
        )}
        {mainTab === 'history' && settleType === 'rebate' && (
          <RebateHistorySection
            isAdmin={canWrite}
            refreshTrigger={historyRefresh}
            giftAgentView={giftAgentView}
          />
        )}
      </div>
    </div>
  );
};
